import { ADMIN_ENDPOINTS, ADMIN_REPORTS_ENDPOINTS } from "../../urls/endpoints";
import { apiCaller } from "../../../helper";
import {
  USER_GROUP_PROGRAM_CODE,
  GROUP_TABLE_SORT_KEYS,
  GROUP_USERS_SORT_KEYS,
} from "../../../constants/userGroup";

const buildSort = (sortCol, sortDir, whitelist) => {
  if (!sortCol || !whitelist.includes(sortCol)) return {};
  return {
    sort: {
      attributes: [sortCol],
      sorts: [sortDir === "desc" ? "desc" : "asc"],
    },
  };
};

const buildEntityPair = ({ entityType, entityId } = {}) => {
  const hasType = Boolean(entityType);
  const hasId = entityId !== null && entityId !== undefined && entityId !== "";
  if (!hasType || !hasId) return {};
  return { entityType, entityId: Number(entityId) };
};

/**
 * Admin User Management API Functions
 */
export const adminUserManagementApi = {
  /**
   * Fetch users table data
   * @param {Object} params - Table parameters
   * @returns {Promise} 
   */
  fetchUsersTable: async (params = {}) => {
    const payload = {
      type: params.type || "asc",
      currentPage: params.currentPage || 0,
      search: params.search || "",
      searchCol: params.searchCol || "",
      sortCol: params.sortCol || "",
      programCode: "USERS",
      exportFlag: params.exportFlag || false,
      filters: params.filters || {},
    };
    return await apiCaller(ADMIN_ENDPOINTS.VIEW_TABLE, "POST", payload);
  },

  /**
   * Create a new user
   * @param {Object} userData - User data
   * @returns {Promise} 
   */
  createUser: async (userData) => {
    const payload = {
      programCode: "USERS",
      data: {
        name: userData.name,
        phone: userData.phone,
        ugId: userData.ugId,
        ...(userData.ugStatus ? { ugStatus: userData.ugStatus } : {}),
        mapping: userData.mapping || [],
      },
    };
    return await apiCaller(ADMIN_ENDPOINTS.CREATE_USER, "POST", payload);
  },

  /**
   * View user details
   * @param {number} userId - User ID
   * @returns {Promise} 
   */
  viewUser: async (userId) => {
    const payload = {
      programCode: "USERS",
      data: {
        userId,
      },
    };
    return await apiCaller(ADMIN_ENDPOINTS.VIEW_USER, "POST", payload);
  },

  /**
   * Update user
   * @param {number} uId - User ID
   * @param {Object} userData - User data (name, phone, ugId, mapping)
   * @returns {Promise} 
   */
  updateUser: async (uId, userData) => {
    const payload = {
      programCode: "USERS",
      uId: uId,
      data: userData,
    };
    return await apiCaller(ADMIN_ENDPOINTS.UPDATE_USER, "PUT", payload);
  },

  /**
   * Create a new group
   * @param {Object} groupData - { ugName, ugDesignation, ugCode, status?, entityType?, entityId? }
   * @returns {Promise}
   */
  createGroup: async (groupData) => {
    const payload = {
      programCode: USER_GROUP_PROGRAM_CODE,
      data: {
        ugName: groupData.ugName,
        ugDesignation: groupData.ugDesignation,
        ugCode: groupData.ugCode,
        ...(groupData.status ? { status: groupData.status } : {}),
        ...buildEntityPair(groupData),
      },
    };
    return await apiCaller(ADMIN_ENDPOINTS.CREATE_GROUP, "POST", payload);
  },

  /**
   * View group details
   * @param {number} ugId - User Group ID
   * @returns {Promise}
   */
  viewGroup: async (ugId) => {
    const payload = {
      programCode: USER_GROUP_PROGRAM_CODE,
      data: {
        ugId,
      },
    };
    return await apiCaller(ADMIN_ENDPOINTS.VIEW_GROUP, "POST", payload);
  },

  /**
   * Update group — partial patch, pass only the changed fields
   * @param {number} ugId - User Group ID
   * @param {Object} changes
   * @returns {Promise}
   */
  updateGroup: async (ugId, changes = {}) => {
    const payload = {
      programCode: USER_GROUP_PROGRAM_CODE,
      data: {
        ugId,
        ...changes,
      },
    };
    return await apiCaller(ADMIN_ENDPOINTS.UPDATE_GROUP, "PUT", payload);
  },

  /**
   * Get group rights/permissions
   * @param {number} ugId - User Group ID
   * @returns {Promise}
   */
  getGroupRights: async (ugId) => {
    const payload = {
      programCode: USER_GROUP_PROGRAM_CODE,
      data: {
        ugId,
      },
    };
    return await apiCaller(ADMIN_ENDPOINTS.GET_GROUP_RIGHTS, "POST", payload);
  },

  /**
   * Update group rights — full replace, omitted programs are revoked
   * @param {number} ugId - User Group ID
   * @param {Array} rights
   * @returns {Promise}
   */
  updateGroupRights: async (ugId, rights) => {
    const payload = {
      programCode: USER_GROUP_PROGRAM_CODE,
      data: {
        ugId,
        rights,
      },
    };
    return await apiCaller(ADMIN_ENDPOINTS.UPDATE_GROUP_RIGHTS, "PUT", payload);
  },

  /**
   * Fetch user groups table data
   * @param {Object} params - { currentPage (0-based), itemsPerPage, search, status, entityType, sortCol, sortDir, exportFlag }
   * @returns {Promise}
   */
  fetchGroupsTable: async (params = {}) => {
    const payload = {
      programCode: USER_GROUP_PROGRAM_CODE,
      search: params.search || "",
      ...(params.status ? { status: params.status } : {}),
      ...(params.entityType ? { entityType: params.entityType } : {}),
      ...buildSort(params.sortCol, params.sortDir, GROUP_TABLE_SORT_KEYS),
      pageNo: (params.currentPage || 0) + 1,
      itemsPerPage: params.itemsPerPage || 20,
      exportFlag: params.exportFlag || false,
    };
    return await apiCaller(ADMIN_ENDPOINTS.VIEW_GROUP_TABLE, "POST", payload);
  },

  /**
   * Export user groups table data — returns a URL string valid for 5 minutes
   * @param {Object} params - same filters as fetchGroupsTable
   * @returns {Promise}
   */
  exportGroupsTable: async (params = {}) => {
    const response = await adminUserManagementApi.fetchGroupsTable({
      ...params,
      exportFlag: true,
    });

    if (response?.status === "success" && typeof response?.data === "string") {
      const aLink = document.createElement("a");
      aLink.href = response.data;
      aLink.click();
    }

    return response;
  },

  /**
   * Fetch the members of a group
   * @param {Object} params - { ugId, currentPage (0-based), itemsPerPage, search, sortCol, sortDir, exportFlag }
   * @returns {Promise}
   */
  fetchGroupUsers: async (params = {}) => {
    const payload = {
      programCode: USER_GROUP_PROGRAM_CODE,
      data: { ugId: params.ugId },
      search: params.search || "",
      ...buildSort(params.sortCol, params.sortDir, GROUP_USERS_SORT_KEYS),
      pageNo: (params.currentPage || 0) + 1,
      itemsPerPage: params.itemsPerPage || 20,
      exportFlag: params.exportFlag || false,
    };
    return await apiCaller(ADMIN_ENDPOINTS.GROUP_USERS, "POST", payload);
  },

  /**
   * Move users into a group
   * @param {number} ugId - target group
   * @param {number[]} userIds - uId values, NOT loginId
   * @returns {Promise}
   */
  assignUsersToGroup: async (ugId, userIds) => {
    const payload = {
      programCode: USER_GROUP_PROGRAM_CODE,
      data: {
        ugId,
        userIds,
      },
    };
    return await apiCaller(ADMIN_ENDPOINTS.ASSIGN_GROUP_USERS, "POST", payload);
  },

  /**
   * Fetch district-wise map dashboard data
   * @param {string} type - Either 'DCS' or 'FARMER'
   * @returns {Promise} 
   */
  fetchMapDashboardData: async (type) => {
    return await apiCaller(ADMIN_REPORTS_ENDPOINTS.MAP_DASHBOARD_CARDS, "POST", { type });
  },

  /**
   * Fetch district-wise retailer counts for map
   * @returns {Promise} 
   */
  fetchRetailerMapData: async () => {
    return await apiCaller(ADMIN_REPORTS_ENDPOINTS.RETAILER_MAP_COUNTS, "POST", { districtId: "" });
  },

  /**
   * Fetch dashboard cards data
   * @returns {Promise} 
   */
  fetchDashboardCards: async () => {
    return await apiCaller(ADMIN_REPORTS_ENDPOINTS.DASHBOARD_CARDS, "POST", {});
  },

  /**
   * Fetch dashboard report data (milk procurement, milk poured, milk type, leaderboard)
   * @param {Array<{key: string, filters: Object}>} reports 
   * @returns {Promise} 
   */
  fetchDashboardReports: async (reports) => {
    const payload = { reports };
    return await apiCaller(ADMIN_REPORTS_ENDPOINTS.DASHBOARD_REPORTS, "POST", payload);
  },

  /**
   * Farmer name/FID search suggestions
   * @param {Object} params - { unionId, dcsId, search, limit }
   * @returns {Promise}
   */
  fetchFarmerTypeahead: async ({ unionId, dcsId, search, limit = 20 } = {}) => {
    const payload = {
      ...(unionId ? { unionId } : {}),
      ...(dcsId ? { dcsId } : {}),
      search,
      limit,
    };
    return await apiCaller(ADMIN_REPORTS_ENDPOINTS.FARMER_TYPEAHEAD, "POST", payload);
  },
};
